import { cn } from '@heidi/utils';

import { menuSeparatorClasses } from '../styles';
import {
  controlFieldStateText,
  controlTransition,
  controlValueText,
} from '../styles/control';

import {
  commandDialogCommandClasses,
  commandDialogContentClasses,
} from './command.styles';

/**
 * Experimental Command styles - aligns cmdk surfaces with the shared menu
 * recipe.
 */
export const commandItemClassesExperimental = cn(
  "relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-surface-hover data-[selected=true]:text-text-primary data-[disabled=true]:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
);

/** Adds the missing `my-1` and keeps the semantic `bg-border` token. */
export const commandSeparatorClassesExperimental = menuSeparatorClasses;

// The experimental field carries the control-tier colour treatment (value /
// placeholder tokens + transition), gated behind the forms flag; the stable
// field uses the `text-*` tokens instead. Same `h-9` box as stable.
export const commandInputClassesExperimental = cn(
  'flex h-9 w-full rounded-md bg-transparent py-0 outline-none disabled:cursor-not-allowed',
  controlValueText,
  controlFieldStateText,
  controlTransition
);

// cmdk renders the group heading as a `[cmdk-group-heading]` child, so the shared
// `menuLabelClasses` recipe (`px-2 py-1.5 text-xs font-medium text-text-secondary`) is
// applied explicitly.
export const commandGroupClassesExperimental = cn(
  'overflow-hidden p-1 text-text-primary',
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-secondary'
);

// Dialog overrides on top of the large-palette base (keeps the base `h-12` search field):
// - `[&_[cmdk-input]]:pe-8` — reserve room so text clears the modal close (✕) at `end-4`.
// - `[&_[cmdk-input-wrapper]_svg]:size-4.5` — base search icon `size-5` reads a touch large;
//   `size-4.5` against the `h-12` field. Item icons go to the recipe's `size-4` below.
// - `[&_[cmdk-item]_svg]:size-4` — base forces item icons to `size-5`; bring them to `size-4`.
// - `[&_[cmdk-group]]:p-2` — concentric gutter: the container is `rounded-xl` (16px) and
//   items are `rounded-md` (8px), so the item gutter must be 16 − 8 = 8px (`p-2`) for the
//   corners to nest (make-interfaces-feel-better: outer = inner + padding).
// - `[&_[cmdk-item]]:py-2.5` — base `py-3` was too tall; tighten the rows
//   (`rounded-md` + `px-2` already match the non-dialog command).
export const commandDialogCommandClassesExperimental = cn(
  commandDialogCommandClasses,
  '[&_[cmdk-group]]:p-2 [&_[cmdk-input-wrapper]_svg]:size-4.5 [&_[cmdk-input]]:pe-8 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item]_svg]:size-4'
);

// The dialog content box inherits `sm:rounded-2xl` + `border` from the base DialogContent.
// `sm:rounded-xl` (16px) is the container radius; concentric with the `rounded-md` (8px)
// items via the group's `p-2` (8px) gutter. `border-0` — the shadow does the elevation.
// Appended after the base, so tailwind-merge keeps these.
export const commandDialogContentClassesExperimental = cn(
  commandDialogContentClasses,
  'border-0 sm:rounded-xl'
);

// Experimental counterpart of the banner radius: matches this variant's
// `sm:rounded-xl` content, not stable's `sm:rounded-2xl`.
export const commandDialogBannerCommandClassesExperimental = cn('sm:rounded-xl');
